import { inject, injectable } from 'inversify';
import { action, observable, reaction } from 'mobx';
import { CurrentProgressResponse, ProgressApi } from '../api/ProgressApi';
import { UserApi } from '../api/UserApi';
import { HttpClient } from '../configs/axios.config';
import { STORAGE_CURRENT_COURSE_NAME, STORAGE_TOKEN_NAME } from '../constants/base.constants';
import { UserModel } from '../interfaces/models/UserModel';
import Logger from '../utils/Logger';
import { ILocalStorageAdapter, LocalStorageSymbol } from './adapters/ILocalStorageAdapter';
import { CourseStore } from './CourseStore';
import moment from "moment";
import {AchievementModel} from "../interfaces/models/AchievementModel";

const hexEncode = (s: string) => {
    let str = '';
    for (let i = 0; i < s.length; i++) {
        str += s[i].charCodeAt(0).toString(16);
    }
    return str;
};

@injectable()
export class UserStore {

    @observable user: UserModel;
    @observable isReady = false;
    @observable progress: CurrentProgressResponse;
    @observable achievements: AchievementModel[] = [];

    userUpdatedDate: Date;
    isProfileUpdated = false;

    /**
     * Get PouchDB user's database name
     */
    get userDb() {
        return 'userdb-' + hexEncode(this.user.username);
    }

    /**
     * Get PouchDB session token
     */
    async getDatabaseToken() {
        if (moment().diff(this.userUpdatedDate, 'minutes') >= 9) {
            await this.loadProfile();
        }

        return this.user.session;
    }

    constructor(
        private httpClient: HttpClient,
        private userApi: UserApi,
        private progressApi: ProgressApi,
        private courseStore: CourseStore,
        @inject(LocalStorageSymbol) private localStorageAdapter: ILocalStorageAdapter,
    ) {
        reaction(() => httpClient.token, () => {
            this.user = null;
        });

        this.initialize();
    }

    async initialize() {
        const token = await this.localStorageAdapter.read(STORAGE_TOKEN_NAME);

        if (token) {
            return this.setToken(token);
        }
    }

    @action.bound async loadProgress() {
        const response = await this.progressApi.getCurrent(this.courseStore.currentCourse.id);
        this.progress = response.data;
    }

    async loadProfile() {
        try {
            const response = await this.userApi.profile();

            this.user = response.data;

            this.userUpdatedDate = new Date();
        } catch (e) {
            Logger.error(e);
        }
    }

    async logout() {
        try {
            // const response = await this.userApi.logout();

            await this.resetToken();
        } catch (e) {
            Logger.error(e);
        }
    }

    @action
    async closeIntro() {
        this.user.intro_finished = true;
        this.userApi.setOption('ig_option_intro', '1');
    }

    async setToken(token) {
        this.httpClient.token = token;
        return this.httpClient.refreshToken();
    }

    async resetToken() {
        await this.localStorageAdapter.remove(STORAGE_CURRENT_COURSE_NAME);
        await this.localStorageAdapter.remove(STORAGE_TOKEN_NAME);

        this.user = null;
        this.httpClient.token = null;
    }

}
